import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import NodeCard from '../ui/NodeCard';
import ToolLogo from '../ui/ToolLogo';
import { LogoSlug } from '../../data/logos';

type Accent = 'cyan' | 'magenta' | 'green' | 'purple' | 'amber';

interface Service {
  title: string;
  tag: string;
  badge: string;
  accent: Accent;
  icon: React.ReactNode;
  description: string;
  deliverables: string[];
  tools: LogoSlug[];
}

const services: Service[] = [
  {
    title: 'Full-Stack SaaS Products',
    tag: 'product.build',
    badge: 'end-to-end',
    accent: 'purple',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 20h8M12 17v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M7.5 9.5 9.5 11l-2 1.5M12 13h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    description:
      'Complete products from schema to billing: React frontends, async Python APIs, auth, subscriptions and tests. I build them solo, ship them and keep them running.',
    deliverables: [
      'React + FastAPI architecture',
      'JWT auth & role-based access',
      'Stripe subscriptions & metering',
      'Automated test suites',
    ],
    tools: ['react', 'fastapi', 'python', 'mongodb', 'stripe'],
  },
  {
    title: 'AI Agents, Chatbots & Voice',
    tag: 'agents.ai',
    badge: 'intelligent',
    accent: 'green',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="5" y="7" width="14" height="11" rx="3" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 4v3M9 12h.01M15 12h.01M9.5 15.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    description:
      'LLM agents and AI voice receptionists that qualify leads, answer questions and take action — wired straight into your CRM.',
    deliverables: [
      'RAG knowledge-base bots',
      'AI voice receptionists & IVR',
      'Tool-calling agents & MCP servers',
      'Prompt guardrails & output validation',
    ],
    tools: ['anthropic', 'openai', 'googlegemini', 'elevenlabs', 'langchain'],
  },
  {
    title: 'n8n Workflow Automation',
    tag: 'workflow.engine',
    badge: 'core',
    accent: 'magenta',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="5" cy="12" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="6" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="18" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="19" cy="12" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <path d="M7 11l3-3M7 13l3 3M14 7l3 3M14 17l3-3" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
    description:
      'Custom multi-step workflows that connect every app you use — self-hosted or cloud, with proper error handling and retries.',
    deliverables: [
      'Trigger → action pipelines',
      'Self-hosted n8n setup',
      'Error handling & retries',
      'Scheduled & webhook jobs',
    ],
    tools: ['n8n', 'make', 'zapier', 'slack'],
  },
  {
    title: 'GoHighLevel CRM & Automation',
    tag: 'ghl.crm',
    badge: 'specialty',
    accent: 'cyan',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M4 5h16l-6 7v6l-4 2v-8L4 5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ),
    description:
      'End-to-end GoHighLevel builds — pipelines, funnels, calendars, and automations that turn leads into booked appointments.',
    deliverables: [
      'Pipelines & opportunity stages',
      'Funnels, forms & calendars',
      'Workflow & trigger automations',
      'Snapshots & sub-account setup',
    ],
    tools: ['gohighlevel', 'twilio', 'calendly', 'hubspot'],
  },
  {
    title: 'Internal Platforms & Portals',
    tag: 'ops.portal',
    badge: 'business software',
    accent: 'amber',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M14 17.5h7M17.5 14v7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    description:
      'Operations software for the work your CRM handles badly: referrals, commissions, org charts, training and rep onboarding, synced back to GoHighLevel.',
    deliverables: [
      'Rep / Team / Admin access tiers',
      'Referral & affiliate tracking',
      'Commission calculation engines',
      'Training, SOPs & digital business cards',
    ],
    tools: ['react', 'python', 'gohighlevel', 'postgresql'],
  },
  {
    title: 'API & App Integrations',
    tag: 'integrations.glue',
    badge: 'connect',
    accent: 'purple',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M8 7l-4 5 4 5M16 7l4 5-4 5M13 5l-2 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    description:
      'Connect anything to anything. REST APIs, webhooks and custom code nodes that keep your tools perfectly in sync.',
    deliverables: [
      'REST / webhook integrations',
      'OAuth & auth handling',
      'Two-way data sync',
      'Custom code & function nodes',
    ],
    tools: ['python', 'fastapi', 'nodedotjs', 'stripe', 'airtable'],
  },
  {
    title: 'Data Scraping & ETL Pipelines',
    tag: 'data.pipeline',
    badge: 'volume',
    accent: 'amber',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <ellipse cx="12" cy="6" rx="7" ry="3" stroke="currentColor" strokeWidth="1.6" />
        <path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ),
    description:
      'Large-scale data acquisition and clean-up — property feeds, directories, documents — landed straight into your dashboards.',
    deliverables: [
      'Selenium & Apify scrapers',
      'Zillow & HUD property feeds',
      'PDF & document extraction',
      'Sheets and CRM reporting dashboards',
    ],
    tools: ['selenium', 'python', 'googlesheets', 'postgresql', 'mongodb'],
  },
  {
    title: 'Paid Media & Tracking Setup',
    tag: 'growth.attribution',
    badge: 'closed-loop',
    accent: 'magenta',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M4 19V11M10 19V5M16 19v-6M20.5 19h-17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    description:
      'Meta and Google Ads with the measurement stack to match — so spend is judged on booked calls, not clicks.',
    deliverables: [
      'Meta & Google Ads management',
      'GA4, GTM & Meta Pixel events',
      'Click → CRM → conversation attribution',
      'Landing pages that match the ad',
    ],
    tools: ['meta', 'googleads', 'googleanalytics', 'gohighlevel', 'wordpress'],
  },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const ServicesSection: React.FC = () => {
  const { themeConfig } = useTheme();

  return (
    <section id="services" className={themeConfig.layout?.sectionStyle}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="mb-12 max-w-2xl"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="h-px w-6 bg-neon-cyan/60" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-neon-cyan">
            {'// what i build'}
          </span>
        </div>
        <h2 className="font-display text-3xl font-bold leading-tight text-ink-main sm:text-4xl">
          Services wired as <span className="gradient-text">nodes</span>
        </h2>
        <p className="mt-3 text-lg text-ink-soft">
          Eight building blocks, from full products to the automations around
          them. Use one, or combine them into a complete system.
        </p>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 gap-6 md:grid-cols-2"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-100px' }}
      >
        {services.map((s) => (
          <motion.div key={s.title} variants={item}>
            <NodeCard
              title={s.title}
              subtitle={s.tag}
              badge={s.badge}
              icon={s.icon}
              accent={s.accent}
              ports
              className="flex h-full flex-col"
            >
              <p className="mb-4 text-sm leading-relaxed text-ink-soft">
                {s.description}
              </p>
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {s.deliverables.map((d) => (
                  <li
                    key={d}
                    className="flex items-start gap-2 font-mono text-xs leading-snug text-ink-soft"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="mt-[2px] shrink-0">
                      <path d="M5 12l4 4L19 6" stroke="#2bffb0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {d}
                  </li>
                ))}
              </ul>
              <div className="mt-auto flex flex-wrap gap-1.5 pt-5">
                {s.tools.map((t) => (
                  <ToolLogo key={t} slug={t} showLabel={false} size={18} />
                ))}
              </div>
            </NodeCard>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

export default ServicesSection;
